/**
 * Virtual Bar Component
 * Order drinks that give temporary visual effects to your avatar
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { X, Wine, Beer, Coffee, Sparkles } from 'lucide-react';

const DRINKS = [
  { 
    id: 'neon_cocktail', 
    name: 'Neon Cocktail', 
    icon: '🍸', 
    price: 10, 
    effect: 'glow',
    effectColor: '#00ff88',
    duration: 60,
    description: 'Adds a green glow to your avatar'
  },
  { 
    id: 'purple_haze', 
    name: 'Purple Haze', 
    icon: '🍹', 
    price: 15, 
    effect: 'glow',
    effectColor: '#a855f7',
    duration: 90,
    description: 'Surround yourself with purple energy'
  },
  { 
    id: 'fire_shot', 
    name: 'Fire Shot', 
    icon: '🥃', 
    price: 20, 
    effect: 'flames',
    effectColor: '#ff6b35',
    duration: 45,
    description: 'Your avatar burns with passion!'
  },
  { 
    id: 'ice_breaker', 
    name: 'Ice Breaker', 
    icon: '🧊', 
    price: 15, 
    effect: 'sparkle',
    effectColor: '#67e8f9',
    duration: 60,
    description: 'Sparkle with cool ice crystals'
  },
  { 
    id: 'golden_elixir', 
    name: 'Golden Elixir', 
    icon: '✨', 
    price: 50, 
    effect: 'rainbow',
    effectColor: '#fbbf24',
    duration: 120,
    description: 'The ultimate VIP drink - rainbow aura!'
  },
  { 
    id: 'energy_boost', 
    name: 'Energy Boost', 
    icon: '⚡', 
    price: 5, 
    effect: 'speed',
    effectColor: '#facc15',
    duration: 30,
    description: 'Move faster for 30 seconds'
  },
  { 
    id: 'rtg_soda', 
    name: 'RTG Flavored Soda', 
    icon: '🍄', 
    price: 100, 
    effect: 'acid_trip',
    effectColor: '#FF00FF',
    duration: 300,
    description: 'WARNING: Full psychedelic trip - 5 min of kaleidoscopes that get heavier and heavier'
  },
  { 
    id: 'turtle_coffee', 
    name: 'Turtle\'s Coffee', 
    icon: '🐢', 
    price: 75, 
    effect: 'turtle_transform',
    effectColor: '#2D6A4F',
    duration: 180,
    description: 'Transforms your character into a turtle for 3 minutes!'
  },
];

const VirtualBar = ({ isOpen, onClose, vibecoins, onPurchase, activeDrink }) => {
  if (!isOpen) return null;

  const handlePurchase = (drink) => {
    if (vibecoins >= drink.price) {
      onPurchase(drink);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-gradient-to-b from-gray-900 via-purple-900/30 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wine className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-bold text-white">Virtual Bar</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-yellow-500/20 px-3 py-1 rounded-full">
              <span className="text-yellow-400">💰</span>
              <span className="text-yellow-300 font-bold">{vibecoins}</span>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Active Effect */}
        {activeDrink && (
          <div className="mx-4 mt-4 p-3 bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-xl border border-purple-500/30">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{activeDrink.icon}</span>
              <div>
                <p className="text-white font-medium">{activeDrink.name} Active!</p>
                <p className="text-white/60 text-xs">Effect expires in {activeDrink.remainingTime}s</p>
              </div>
            </div>
          </div>
        )}

        {/* Drinks Grid */}
        <div className="p-4 grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
          {DRINKS.map((drink) => {
            const canAfford = vibecoins >= drink.price;
            return (
              <motion.button
                key={drink.id}
                whileHover={{ scale: canAfford ? 1.02 : 1 }}
                whileTap={{ scale: canAfford ? 0.98 : 1 }}
                onClick={() => handlePurchase(drink)}
                disabled={!canAfford}
                data-testid={`drink-${drink.id}`}
                className={`relative p-4 rounded-xl border text-left transition-all overflow-hidden ${
                  drink.id === 'rtg_soda'
                    ? canAfford
                      ? 'col-span-2 border-fuchsia-500/50 bg-gradient-to-r from-fuchsia-900/40 via-purple-900/30 to-indigo-900/40 hover:from-fuchsia-900/50 hover:to-indigo-900/50 hover:border-fuchsia-400/70'
                      : 'col-span-2 border-gray-700/30 bg-black/20 opacity-50 cursor-not-allowed'
                    : drink.id === 'turtle_coffee'
                    ? canAfford
                      ? 'col-span-2 border-emerald-500/50 bg-gradient-to-r from-emerald-900/40 via-green-900/30 to-teal-900/40 hover:from-emerald-900/50 hover:to-teal-900/50 hover:border-emerald-400/70'
                      : 'col-span-2 border-gray-700/30 bg-black/20 opacity-50 cursor-not-allowed'
                    : canAfford
                    ? 'border-purple-500/30 bg-black/30 hover:bg-purple-500/10 hover:border-purple-500/50'
                    : 'border-gray-700/30 bg-black/20 opacity-50 cursor-not-allowed'
                }`}
              >
                {/* Glow effect for special drinks */}
                {drink.id === 'rtg_soda' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-500/10 via-transparent to-purple-500/10 animate-pulse" />
                )}
                {drink.id === 'turtle_coffee' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-teal-500/10 animate-pulse" />
                )}
                <div className="relative">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-3xl">{drink.icon}</span>
                    <div className="flex items-center gap-1 text-yellow-400 text-sm font-bold">
                      <span>💰</span>
                      <span>{drink.price}</span>
                    </div>
                  </div>
                  <h3 className={`font-medium text-sm ${drink.id === 'rtg_soda' ? 'text-fuchsia-300' : drink.id === 'turtle_coffee' ? 'text-emerald-300' : 'text-white'}`}>{drink.name}</h3>
                  <p className="text-white/50 text-xs mt-1">{drink.description}</p>
                  <div className={`mt-2 text-xs ${drink.id === 'rtg_soda' ? 'text-fuchsia-400' : drink.id === 'turtle_coffee' ? 'text-emerald-400' : 'text-purple-400'}`}>
                    ⏱️ {drink.duration >= 60 ? `${Math.floor(drink.duration / 60)}m${drink.duration % 60 ? ` ${drink.duration % 60}s` : ''}` : `${drink.duration}s`}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-purple-500/20 bg-black/30">
          <p className="text-center text-white/40 text-xs">
            Drink effects are temporary and visible to all players
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export { VirtualBar, DRINKS };
export default VirtualBar;
